import React, { useState } from "react";
import {
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    TableSortLabel,
    Grid,
    TableContainer,
    TextField,
} from "@material-ui/core";
import firebase from "firebase/app";
import "firebase/database";
import {
    esFechaMayorAHaceNDiasHabiles,
    esFinDeSemana,
    siguienteDiaHabil,
} from "../../../../services/utils";
import Background from "../../../../assets/backgroundPhotos/fondogris_Dashport.jpg";
import Cotizacion from "./Cotizacion";
import EditableField from "../../../EditableField/EditableField";
import CSVButton from "../Reports/CSVButton";
import { CSVLink } from "react-csv";

const currencyFormat = new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
});

const percentFormat = (value) => (value * 100).toFixed(2) + "%";

const compactCellStyle = {
    padding: "4px 8px",
    fontSize: "0.82rem",
    verticalAlign: "top",
    whiteSpace: "nowrap",
    background: "#fff",
};

const compactHeaderCellStyle = {
    padding: "6px 8px",
    fontSize: "0.8rem",
    fontWeight: 700,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    background: "#fafafa",
};

const stickyFirstColumnBodyStyle = {
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: "#fff",
    boxShadow: "2px 0 4px rgba(0,0,0,0.08)",
};

const stickyFirstColumnHeaderStyle = {
    position: "sticky",
    left: 0,
    zIndex: 5,
    background: "#fafafa",
    boxShadow: "2px 0 4px rgba(0,0,0,0.08)",
};

const formatFechaCorta = (fecha) =>
    fecha.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit" });

// Construye la lista de "numeroDeDias" días calendario consecutivos (de
// mañana en adelante, incluyendo fines de semana). El número de días ya
// viene calculado de antemano (ver calcularTotales) a partir de cuántos
// días le hacen falta a la transacción más reciente de todos los eventos
// para estar disponible — así el último día de la lista coincide con esa
// fecha de disponibilidad real.
function construirFechasFuturasNecesarias(numeroDeDias, fechaBase) {
    if (!numeroDeDias || numeroDeDias <= 0) return [];
    const fechas = [];
    for (let i = 1; i <= numeroDeDias; i++) {
        const fecha = new Date(fechaBase);
        fecha.setDate(fecha.getDate() + i);
        fechas.push(fecha);
    }
    return fechas;
}

// Cuántos días calendario (desde hoyBase) le faltan a un pago para estar
// disponible — probando día por día contra la regla real
// (esFechaMayorAHaceNDiasHabiles), en vez de usar diasHabilesFaltantes
// (que cuenta días hábiles desde la fecha EXACTA del pago y por eso no
// distingue pagos de viernes/sábado/domingo entre sí). Con 5 días hábiles
// nunca hacen falta más de ~9 días corridos, pero se pone un tope de
// seguridad generoso.
function diasCalendarioFaltantesPara(pagoTS, hoyBase) {
    if (esFechaMayorAHaceNDiasHabiles(pagoTS, 5, hoyBase).resultado) return 0;
    let probe = new Date(hoyBase);
    for (let i = 1; i <= 15; i++) {
        probe = new Date(probe);
        probe.setDate(probe.getDate() + 1);
        if (esFechaMayorAHaceNDiasHabiles(pagoTS, 5, probe).resultado) return i;
    }
    return 15;
}

const weekendHeaderCellStyle = {
    ...compactHeaderCellStyle,
    background: "#e0e0e0",
};

const weekendCellStyle = {
    ...compactCellStyle,
    background: "#e0e0e0",
};

const weekendTotalCellStyle = {
    ...compactCellStyle,
    background: "#e0e0e0",
};

const errorCellStyle = {
    padding: "4px 8px",
    fontSize: "0.82rem",
    verticalAlign: "top",
    whiteSpace: "normal",
    color: "#b71c1c",
    background: "#fff3f3",
};

const eventNameCellStyle = {
    padding: "4px 8px",
    fontSize: "0.82rem",
    verticalAlign: "top",
    lineHeight: 1.12,
    minWidth: 220,
    maxWidth: 260,
    whiteSpace: "normal",
    wordBreak: "break-word",
    background: "#fff",
};

const totalRowStickyStyle = {
    position: "sticky",
    left: 0,
    zIndex: 3,
    background: "#f5f5f5",
    boxShadow: "2px 0 4px rgba(0,0,0,0.08)",
};

const TotalesEventos = ({ listaEventosAll, businessIdProp }) => {
    const hoy = new Date();
    hoy.setMonth(hoy.getMonth() - 1);
    const fechaLimite = hoy.toISOString().slice(0, 10);

    const [totales, setTotales] = useState([]);
    const [loading, setLoading] = useState(false);
    const [businessId, setBusinessId] = useState(
        businessIdProp === "vivedeporte" ? "vivedeporte.mx" : businessIdProp
    );
    const [sortConfig, setSortConfig] = useState({
        key: "neto",
        direction: "desc",
    });
    const [listToDownload, setListToDownload] = useState([]);

    const [depositsToDownload, setDepositsToDownload] = useState([]);

    const [selectedEventos, setSelectedEventos] = useState([]);

    // Días calendario (de mañana en adelante) que se necesitan para que la
    // última columna ya muestre el total disponible de todas las
    // transacciones, incluida la más reciente. Se recalcula en cada
    // "Calcular totales" según los datos reales (ver calcularTotales).
    const [fechasDisponibilidadFutura, setFechasDisponibilidadFutura] = useState([]);

    const fechaFinDefault = new Date();
    fechaFinDefault.setFullYear(fechaFinDefault.getFullYear() + 1);

    const [fechaInicio, setFechaInicio] = useState(fechaLimite);
    const [fechaFin, setFechaFin] = useState(
        fechaFinDefault.toISOString().slice(0, 10)
    );

    const toggleEvento = (iDEvento) => {
        const id = String(iDEvento);
        setSelectedEventos((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const seleccionarTodos = () => {
        setSelectedEventos(totales.map((t) => String(t.iDEvento)));
    };

    const deseleccionarTodos = () => {
        setSelectedEventos([]);
    };

    const handleSort = (key) => {
        const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
        setSortConfig({ key, direction: isAsc ? "desc" : "asc" });
    };

    const setBusinessIdFunc = (data) => {
        const { businessId } = data;
        setBusinessId(businessId);
    };

    const calcularTotales = async () => {
        setLoading(true);
        const nuevosTotales = [];

        let listaEventos = [];

        if (listaEventosAll) {
            listaEventos = Object.values(listaEventosAll).filter((a) => {
                if (businessIdProp === "dashport") {
                    const cumpleFechaInicio =
                        !fechaInicio || a.fechaEvento >= fechaInicio;

                    const cumpleFechaFin =
                        !fechaFin || a.fechaEvento <= fechaFin;

                    const cumpleBusiness =
                        !businessId || a.businessId === businessId;

                    return (
                        cumpleFechaInicio &&
                        cumpleFechaFin &&
                        cumpleBusiness
                    );
                }

                const cumpleFecha = a.fechaEvento > fechaLimite;
                const cumpleBusiness =
                    !businessId || a.businessId === businessId;

                return cumpleFecha && cumpleBusiness;
            });
        }

        let listadoInscritosToExport = [];
        let depositsList = [];

        const hoyBase = new Date();
        hoyBase.setHours(0, 0, 0, 0);

        // Pasada 1: trae y cachea deposits/inscritos de cada evento (un solo
        // viaje a Firebase por evento) y de paso detecta, entre todas las
        // transacciones de todos los eventos, cuántos días calendario le
        // faltan a la más reciente para estar disponible. Ese máximo
        // determina cuántas columnas de "próximos días" hacen falta para
        // que la última ya muestre el total completo del evento.
        const eventosCache = [];
        let maxDiasCalendarioFaltantesGlobal = 0;

        for (const evento of listaEventos) {
            const { iDEvento, nombreEvento, fechaEvento } = evento;
            const eventoIndex = "evento_" + iDEvento;

            try {
                const depositsSnapshot = await firebase
                    .database()
                    .ref(`eventosCompletos/${eventoIndex}/deposits/depositsList`)
                    .once("value");

                const inscritosSnapshot = await firebase
                    .database()
                    .ref(`eventosCompletos/${eventoIndex}/inscritos/listadoInscritos`)
                    .once("value");

                const deposits = depositsSnapshot.val() || {};
                const inscritos = inscritosSnapshot.val() || {};

                for (const key in inscritos) {
                    const p = inscritos[key];
                    const pago = p.statusPago || {};
                    const forma = pago.formaPago;
                    if (
                        pago.status === "ok" &&
                        !p.borrado &&
                        (forma === "oxxo_pay" || forma === "card")
                    ) {
                        const diasFaltantes = diasCalendarioFaltantesPara(
                            p.serverTime,
                            hoyBase
                        );
                        if (diasFaltantes > maxDiasCalendarioFaltantesGlobal) {
                            maxDiasCalendarioFaltantesGlobal = diasFaltantes;
                        }
                    }
                }

                eventosCache.push({ iDEvento, nombreEvento, fechaEvento, deposits, inscritos });
            } catch (error) {
                // Pasa normalmente cuando el evento aún no tiene el reporte de
                // deposits generado, y por ende tampoco se le asignó acceso a
                // ese path al usuario del negocio. Se muestra el evento sin
                // montos en vez de tronar todo el cálculo y dejar la pantalla
                // sin reporte.
                eventosCache.push({
                    iDEvento,
                    nombreEvento,
                    fechaEvento,
                    sinAcceso: true,
                    mensajeError:
                        (error && error.message) ||
                        "No tienes acceso al reporte de depósitos de este evento.",
                });
            }
        }

        const fechasFuturasCalculadas = construirFechasFuturasNecesarias(
            maxDiasCalendarioFaltantesGlobal,
            hoyBase
        );
        setFechasDisponibilidadFutura(fechasFuturasCalculadas);

        // Pasada 2: con los datos ya cacheados, calcula los totales de cada
        // evento usando las columnas futuras recién determinadas.
        for (const cacheEntry of eventosCache) {
            if (cacheEntry.sinAcceso) {
                nuevosTotales.push(cacheEntry);
                continue;
            }

            const { iDEvento, nombreEvento, fechaEvento, deposits, inscritos } = cacheEntry;
            const eventoIndex = "evento_" + iDEvento;

            let oxxoPercentSum = 0;
            let cardPercentSum = 0;
            let totalDepositos = 0;
            let otrosIngresos = 0;

            for (const key in deposits) {
                const d = deposits[key];
                if (d.type === "oxxoRegistryPercent") oxxoPercentSum += Number(d.amount || 0);
                if (d.type === "cardRegistryPercent") cardPercentSum += Number(d.amount || 0);
                if (d.type === "deposit") totalDepositos += Number(d.amount || 0);
                if (d.type === "fixedAmount") otrosIngresos += Number(d.amount || 0);
                depositsList.push({ ...d, iDEvento: iDEvento, nombreEvento: nombreEvento });
            }

            let totalOxxo = 0;
            let totalCard = 0;
            let totalOxxoDisponible = 0;
            let totalCardDisponible = 0;
            // Monto que estará disponible en cada una de las fechas futuras
            // calculadas (fechasFuturasCalculadas), separado por forma de pago.
            let disponibleFuturoOxxo = fechasFuturasCalculadas.map(() => 0);
            let disponibleFuturoCard = fechasFuturasCalculadas.map(() => 0);
            let transaccionesCount = 0;
            let montoTransaccionesBruto = 0;

            for (const key in inscritos) {
                const p = inscritos[key];
                const pago = p.statusPago || {};
                const forma = pago.formaPago;
                const status = pago.status;
                const borrado = p.borrado;

                listadoInscritosToExport.push({
                    id: "Dashport " + eventoIndex + " " + key,
                    name: p.nombres,
                    date: p.fechaRegistro,
                    lastname: p.apellidos,
                    correo: p.correo,
                    amount: pago.amount,
                    status: status,
                    formaPago: forma,
                });

                if (status === "ok" && !borrado) {
                    const orden = Array.isArray(p.order) ? p.order : [];
                    const totalOrden = orden
                        .filter((item) => item.code !== "comision")
                        .reduce(
                            (sum, item) => sum + (item.unit_price || 0) * (item.quantity || 1),
                            0
                        );

                    if (forma === "oxxo_pay") totalOxxo += totalOrden;
                    if (forma === "card") totalCard += totalOrden;

                    if (forma === "oxxo_pay" || forma === "card") {
                        transaccionesCount += 1;
                        montoTransaccionesBruto += totalOrden;
                    }

                    const data = esFechaMayorAHaceNDiasHabiles(p.serverTime, 5);
                    const disponible = data.resultado;

                    if (forma === "oxxo_pay" && disponible) totalOxxoDisponible += totalOrden;
                    if (forma === "card" && disponible) totalCardDisponible += totalOrden;

                    for (let idx = 0; idx < fechasFuturasCalculadas.length; idx++) {
                        const fechaFutura = fechasFuturasCalculadas[idx];
                        const disponibleEnFecha = esFechaMayorAHaceNDiasHabiles(
                            p.serverTime,
                            5,
                            fechaFutura
                        ).resultado;
                        if (!disponibleEnFecha) continue;
                        if (forma === "oxxo_pay") disponibleFuturoOxxo[idx] += totalOrden;
                        if (forma === "card") disponibleFuturoCard[idx] += totalOrden;
                    }
                }
            }

            const oxxoMultiplier = oxxoPercentSum > 0 ? oxxoPercentSum / 100 : 1;
            const cardMultiplier = cardPercentSum > 0 ? cardPercentSum / 100 : 1;

            totalOxxo *= oxxoMultiplier;
            totalCard *= cardMultiplier;
            totalOxxoDisponible *= oxxoMultiplier;
            totalCardDisponible *= cardMultiplier;

            disponibleFuturoOxxo = disponibleFuturoOxxo.map((v) => v * oxxoMultiplier);
            disponibleFuturoCard = disponibleFuturoCard.map((v) => v * cardMultiplier);

            const neto = totalOxxo + totalCard + otrosIngresos - totalDepositos;
            const netoDisponible =
                totalOxxoDisponible + totalCardDisponible + otrosIngresos - totalDepositos;
            const netoDisponibleFuturo = fechasFuturasCalculadas.map(
                (_, idx) =>
                    disponibleFuturoOxxo[idx] +
                    disponibleFuturoCard[idx] +
                    otrosIngresos -
                    totalDepositos
            );

            nuevosTotales.push({
                iDEvento,
                nombreEvento,
                fechaEvento,
                oxxoMultiplier,
                cardMultiplier,
                totalOxxo,
                totalCard,
                totalDepositos,
                otrosIngresos,
                neto,
                netoDisponible,
                netoDisponibleFuturo,
                transacciones: transaccionesCount,
                montoTransacciones: montoTransaccionesBruto,
                ticketPromedio:
                    transaccionesCount > 0
                        ? montoTransaccionesBruto / transaccionesCount
                        : 0,
            });
        }

        setTotales(nuevosTotales);
        setSelectedEventos(nuevosTotales.map((t) => String(t.iDEvento)));
        setLoading(false);
        setListToDownload(listadoInscritosToExport);
        setDepositsToDownload(depositsList);
    };

    const sortedTotales = [...totales].sort((a, b) => {
        const { key, direction } = sortConfig;
        const dir = direction === "asc" ? 1 : -1;

        if (key === "fechaEvento") {
            return (new Date(a[key]) - new Date(b[key])) * dir;
        }

        let va;
        let vb;
        if (key.indexOf("netoDisponibleFuturo_") === 0) {
            const idx = Number(key.slice("netoDisponibleFuturo_".length));
            va = a.netoDisponibleFuturo ? a.netoDisponibleFuturo[idx] : undefined;
            vb = b.netoDisponibleFuturo ? b.netoDisponibleFuturo[idx] : undefined;
        } else {
            va = a[key];
            vb = b[key];
        }

        // Las filas sin acceso al reporte de depósitos no tienen los campos
        // numéricos (neto, ticketPromedio, etc.) — se tratan como 0 al ordenar.
        if (
            typeof va === "number" ||
            typeof vb === "number" ||
            va === undefined ||
            vb === undefined
        ) {
            return ((va || 0) - (vb || 0)) * dir;
        }
        return String(va).localeCompare(String(vb)) * dir;
    });

    const totalesFiltrados = totales.filter((t) =>
        selectedEventos.includes(String(t.iDEvento))
    );

    const totalGlobal = {
        totalOxxo: totalesFiltrados.reduce((sum, ev) => sum + (ev.totalOxxo || 0), 0),
        totalCard: totalesFiltrados.reduce((sum, ev) => sum + (ev.totalCard || 0), 0),
        totalDepositos: totalesFiltrados.reduce((sum, ev) => sum + (ev.totalDepositos || 0), 0),
        otrosIngresos: totalesFiltrados.reduce((sum, ev) => sum + (ev.otrosIngresos || 0), 0),
        neto: totalesFiltrados.reduce((sum, ev) => sum + (ev.neto || 0), 0),
        netoDisponible: totalesFiltrados.reduce((sum, ev) => sum + (ev.netoDisponible || 0), 0),
        netoDisponibleFuturo: fechasDisponibilidadFutura.map((_, idx) =>
            totalesFiltrados.reduce(
                (sum, ev) =>
                    sum + ((ev.netoDisponibleFuturo && ev.netoDisponibleFuturo[idx]) || 0),
                0
            )
        ),
        transacciones: totalesFiltrados.reduce((sum, ev) => sum + (ev.transacciones || 0), 0),
        montoTransacciones: totalesFiltrados.reduce(
            (sum, ev) => sum + (ev.montoTransacciones || 0),
            0
        ),
    };
    totalGlobal.ticketPromedio =
        totalGlobal.transacciones > 0
            ? totalGlobal.montoTransacciones / totalGlobal.transacciones
            : 0;
    const eventosSinAcceso = totalesFiltrados.filter((ev) => ev.sinAcceso);

    const renderSortableHeader = (label, key) => (
        <TableSortLabel
            active={sortConfig.key === key}
            direction={sortConfig.key === key ? sortConfig.direction : "asc"}
            onClick={() => handleSort(key)}
        >
            {label}
        </TableSortLabel>
    );

    return (
        <Paper
            style={{
                padding: 16,
                backgroundImage: `url(${Background})`,
            }}
        >
            {businessIdProp === "dashport" && (
                <CSVLink data={listToDownload} filename="reporte.csv">
                    <Button variant="contained" color="primary">
                        Descargar todas las transacciones
                    </Button>
                </CSVLink>
            )}
            {businessIdProp === "dashport" && (
                <CSVLink data={depositsToDownload} filename="reporte.csv">
                    <Button variant="contained" color="primary">
                        Descargar todas los depositos
                    </Button>
                </CSVLink>
            )}

            <Typography variant="h4" style={{ marginTop: 10 }}>
                Totales por evento
            </Typography>

            <Typography variant="subtitle2" style={{ marginBottom: 12 }}>
                Muestra los eventos próximos y los que tengan menos de un mes de haberse realizado.
            </Typography>
            <Typography variant="subtitle2" style={{ marginBottom: 12 }}>
                NOTA: para que un evento aparezca en la lista debe ser marcado con el id del negocio ({businessIdProp}) en la vista de configración del mismo evento
            </Typography>

            <Grid container direction="row" spacing={2} alignItems="center">
                {businessIdProp === "dashport" && (
                    <Grid item xs={12} md={6}>
                        <EditableField
                            fieldObject={{
                                label: "Empresa a revisar",
                                errorMessage: "Campo Requerido",
                                fieldName: "businessId",
                                type: "select",
                                defaultValue: businessId,
                                required: true,
                                regex: "",
                                textSize: "h5",
                                optionsArray: [
                                    { value: "", label: "Todos" },
                                    { value: "vivedeporte.mx", label: "Vivedeporte" },
                                    { value: "volare", label: "Volaré" },
                                    { value: "frogstrailmexico", label: "Frogs Trail México" },
                                    { value: "5oclock", label: "5 OClock" },
                                    { value: "chronosport", label: "Chronosport" },
                                    {
                                        value: "aguasabiertasyucatan",
                                        label: "Aguas Abiertas Yucatán",
                                    },
                                ],
                            }}
                            onSubmit={setBusinessIdFunc}
                        />
                    </Grid>
                )}

                {businessIdProp === "dashport" && (
                    <>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Fecha inicio"
                                type="date"
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="Fecha fin"
                                type="date"
                                value={fechaFin}
                                onChange={(e) => setFechaFin(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    min: fechaInicio || undefined,
                                }}
                            />
                        </Grid>
                    </>
                )}

                <Grid item xs={12} md={6}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => calcularTotales()}
                        disabled={loading}
                        style={{ margin: "10px 0", padding: "8px 14px" }}
                    >
                        Calcular totales
                    </Button>
                </Grid>

                {!loading && totales.length > 0 && (
                    <>
                        <Grid item>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={seleccionarTodos}
                                disabled={selectedEventos.length === totales.length}
                            >
                                Seleccionar todos
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={deseleccionarTodos}
                                disabled={selectedEventos.length === 0}
                            >
                                Deseleccionar todos
                            </Button>
                        </Grid>
                        <Grid item>
                            <Typography variant="caption" color="textSecondary">
                                {selectedEventos.length} de {totales.length} eventos seleccionados
                            </Typography>
                        </Grid>
                    </>
                )}
            </Grid>

          

            {loading && <CircularProgress />}

            {!loading && totales.length > 0 && (
                <TableContainer
                    style={{
                        marginTop: 12,
                        maxHeight: "72vh",
                        overflowX: "auto",
                        background: "rgba(255,255,255,0.94)",
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    style={{
                                        ...compactHeaderCellStyle,
                                        ...stickyFirstColumnHeaderStyle,
                                        minWidth: 240,
                                        maxWidth: 280,
                                    }}
                                >
                                    {renderSortableHeader("Evento", "nombreEvento")}
                                </TableCell>
                                <TableCell style={compactHeaderCellStyle}>
                                    {renderSortableHeader("Fecha", "fechaEvento")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("% Oxxo", "oxxoMultiplier")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("% TC/TD", "cardMultiplier")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("Total Oxxo", "totalOxxo")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("Total TC/TD", "totalCard")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("Depósitos (restar)", "totalDepositos")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("Otros ingresos", "otrosIngresos")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("Transacciones", "transacciones")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("Ticket promedio", "ticketPromedio")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("Neto por evento", "neto")}
                                </TableCell>
                                <TableCell align="right" style={compactHeaderCellStyle}>
                                    {renderSortableHeader("Disponible", "netoDisponible")}
                                </TableCell>
                                {fechasDisponibilidadFutura.map((fecha, idx) => {
                                    const finDeSemana = esFinDeSemana(fecha);
                                    return (
                                        <TableCell
                                            align="right"
                                            style={{
                                                ...(finDeSemana ? weekendHeaderCellStyle : compactHeaderCellStyle),
                                                whiteSpace: "normal",
                                                minWidth: 110,
                                            }}
                                            key={idx}
                                        >
                                            {renderSortableHeader(
                                                "Disponible " + formatFechaCorta(fecha),
                                                "netoDisponibleFuturo_" + idx
                                            )}
                                            {finDeSemana && (
                                                <div
                                                    style={{
                                                        fontSize: "0.65rem",
                                                        fontWeight: 400,
                                                        color: "#ff4400",
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    No hábil para transferir — se podría
                                                    solicitar hasta el{" "}
                                                    {formatFechaCorta(siguienteDiaHabil(fecha))}
                                                </div>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {sortedTotales.map((t, idx) => {
                                const isSelected = selectedEventos.includes(String(t.iDEvento));
                                return (
                                    <TableRow
                                        key={idx}
                                        hover
                                        style={{ opacity: isSelected ? 1 : 0.4 }}
                                    >
                                        <TableCell
                                            style={{
                                                ...eventNameCellStyle,
                                                ...stickyFirstColumnBodyStyle,
                                                background: isSelected ? "#fff" : "#f5f5f5",
                                            }}
                                        >
                                            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                                                <Button
                                                    variant={isSelected ? "contained" : "outlined"}
                                                    color={isSelected ? "primary" : "default"}
                                                    size="small"
                                                    onClick={() => toggleEvento(t.iDEvento)}
                                                    style={{
                                                        minWidth: 28,
                                                        width: 28,
                                                        height: 22,
                                                        padding: 0,
                                                        flexShrink: 0,
                                                        fontSize: "0.7rem",
                                                        marginTop: 1,
                                                    }}
                                                >
                                                    {isSelected ? "✓" : "○"}
                                                </Button>

                                                <a href={`/eventos-organizados/evento_${t.iDEvento}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: "inherit",
                                                        textDecoration: "none",
                                                        fontWeight: 500,
                                                        lineHeight: 1.12,
                                                        display: "inline-block",
                                                    }}
                                                >
                                                    {t.nombreEvento}
                                                </a>
                                            </div>
                                        </TableCell>

                                        {t.sinAcceso ? (
                                            <TableCell colSpan={11 + fechasDisponibilidadFutura.length} style={errorCellStyle}>
                                                Sin acceso al reporte de depósitos de este evento
                                                (aún no se le asignó acceso a tu usuario). No se incluye en los totales.
                                            </TableCell>
                                        ) : (
                                            <>
                                                <TableCell style={compactCellStyle}>{t.fechaEvento}</TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    {percentFormat(t.oxxoMultiplier)}
                                                </TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    {percentFormat(t.cardMultiplier)}
                                                </TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    {currencyFormat.format(t.totalOxxo)}
                                                </TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    {currencyFormat.format(t.totalCard)}
                                                </TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    {currencyFormat.format(t.totalDepositos)}
                                                </TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    {currencyFormat.format(t.otrosIngresos)}
                                                </TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    {t.transacciones}
                                                </TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    {currencyFormat.format(t.ticketPromedio)}
                                                </TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    <strong>{currencyFormat.format(t.neto)}</strong>
                                                </TableCell>
                                                <TableCell align="right" style={compactCellStyle}>
                                                    <strong>{currencyFormat.format(t.netoDisponible)}</strong>
                                                </TableCell>
                                                {t.netoDisponibleFuturo.map((monto, fidx) => {
                                                    const finDeSemana =
                                                        fechasDisponibilidadFutura[fidx] &&
                                                        esFinDeSemana(fechasDisponibilidadFutura[fidx]);
                                                    return (
                                                        <TableCell
                                                            align="right"
                                                            style={finDeSemana ? weekendCellStyle : compactCellStyle}
                                                            key={fidx}
                                                        >
                                                            <strong>{currencyFormat.format(monto)}</strong>
                                                        </TableCell>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </TableRow>
                                );
                            })}

                            <TableRow style={{ backgroundColor: "#f5f5f5"}}>
                                <TableCell
                                    style={{
                                        ...compactCellStyle,
                                        ...totalRowStickyStyle,
                                        fontWeight: 700,
                                         paddingBottom:"30px"
                                        
                                    }}
                                >
                                    <strong>TOTALES  ({selectedEventos.length})</strong>
                                </TableCell>
                                <TableCell colSpan={3} align="right" style={{ ...compactCellStyle, background: "#f5f5f5" }}>
                                </TableCell>
                                <TableCell align="right" style={{ ...compactCellStyle, background: "#f5f5f5" }}>
                                    <strong>{currencyFormat.format(totalGlobal.totalOxxo)}</strong>
                                </TableCell>
                                <TableCell align="right" style={{ ...compactCellStyle, background: "#f5f5f5" }}>
                                    <strong>{currencyFormat.format(totalGlobal.totalCard)}</strong>
                                </TableCell>
                                <TableCell align="right" style={{ ...compactCellStyle, background: "#f5f5f5" }}>
                                    <strong>{currencyFormat.format(totalGlobal.totalDepositos)}</strong>
                                </TableCell>
                                <TableCell align="right" style={{ ...compactCellStyle, background: "#f5f5f5" }}>
                                    <strong>{currencyFormat.format(totalGlobal.otrosIngresos)}</strong>
                                </TableCell>
                                <TableCell align="right" style={{ ...compactCellStyle, background: "#f5f5f5" }}>
                                    <strong>{totalGlobal.transacciones}</strong>
                                </TableCell>
                                <TableCell align="right" style={{ ...compactCellStyle, background: "#f5f5f5" }}>
                                    <strong>{currencyFormat.format(totalGlobal.ticketPromedio)}</strong>
                                </TableCell>
                                <TableCell align="right" style={{ ...compactCellStyle, background: "#f5f5f5" }}>
                                    <strong>{currencyFormat.format(totalGlobal.neto)}</strong>
                                </TableCell>
                                <TableCell align="right" style={{ ...compactCellStyle, background: "#f5f5f5" }}>
                                    <strong>{currencyFormat.format(totalGlobal.netoDisponible)}</strong>
                                </TableCell>
                                {totalGlobal.netoDisponibleFuturo.map((monto, fidx) => {
                                    const finDeSemana =
                                        fechasDisponibilidadFutura[fidx] &&
                                        esFinDeSemana(fechasDisponibilidadFutura[fidx]);
                                    return (
                                        <TableCell
                                            align="right"
                                            style={finDeSemana ? weekendTotalCellStyle : { ...compactCellStyle, background: "#f5f5f5" }}
                                            key={fidx}
                                        >
                                            <strong>{currencyFormat.format(monto)}</strong>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )
            }
        </Paper >
    );
};

export default TotalesEventos;